/**
 * FlashBookHtmlPlayer — protocol v4
 *
 * Renders a FlashBook HTML theme (served from /public/themes/flashbook/ or a
 * hosted URL) inside a sandboxed <iframe> on web. The HTML file communicates
 * with the host via the postMessage bridge protocol documented in
 * docs/flashbook-html-theme.md.
 *
 * -- v4 Protocol --------------------------------------------------------------
 *
 *  iframe -> host
 *    { type: "ready" }
 *    { type: "block_answer", payload: { pageId, blockId, value, timeSpentMs } }
 *    { type: "navigate_next" } / { type: "navigate_prev" } / { type: "navigate_to", payload: { index } }
 *    { type: "complete", payload: { answers?, totalTimeMs? } }
 *    { type: "exit" }
 *
 *  host -> iframe
 *    { type: "init",       payload: BridgeFlashbookInitPayload }
 *    { type: "page_show",  payload: { index, page, total } }
 *    { type: "feedback",   payload: { pageId, blockId, correct, pointsEarned, pointsPossible, correctValue? } }
 *
 * On native (iOS/Android) this renders a "not supported" placeholder because
 * there is no react-native-webview bridge built yet.
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'

import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {type SlideAnswerResult} from '#/components/capsules/play/types'
import {
  type BridgeSlide,
  checkAnswer,
  correctValueFor,
  mapSlide,
} from '#/themes/html-bridge/HtmlBridgePlayer'
import {type FlashBookModel, type FlashBookPage} from '#/types/flashbook'

// -- Bridge types -------------------------------------------------------------

export interface BridgeFlashbookInitPayload {
  protocolVersion: 4
  mode: 'play' | 'preview'
  locale: string
  isRtl: boolean
  user: {
    id: string
    handle: string
    displayName: string
    avatarUri: string | null
  }
  behaviour: {
    instantFeedback: boolean
    shuffleOptions: boolean
    showProgressBar: boolean
  }
  book: {
    id: string
    title: string
    description: string
    authorHandle: string
    authorDisplayName: string
    coverImageUri?: string
  }
  pages: {id: string; title?: string; blocks: BridgeSlide[]}[]
  totalPages: number
  totalPoints: number
}

type Props = {
  book: FlashBookModel
  /** Pages to render (already sliced for single-page previews). */
  pages: FlashBookPage[]
  onBlockResult: (
    pageId: string,
    blockId: string,
    blockType: string,
    result: SlideAnswerResult,
  ) => void
  onComplete: () => void
  onClose: () => void
}

type PlayerState = 'idle' | 'loading' | 'running' | 'error'

export function FlashBookHtmlPlayer({
  book,
  pages,
  onBlockResult,
  onComplete,
  onClose,
}: Props) {
  const settings = book.behaviorSettings ?? {}
  const themeUrl = (settings.htmlThemeUrl as string | undefined) ?? ''

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [srcUrl] = useState<string | null>(() =>
    Platform.OS === 'web' ? themeUrl || null : null,
  )
  /** Bumped to force a fresh iframe mount when the learner retries. */
  const [reloadKey, setReloadKey] = useState(0)
  const [state, setState] = useState<PlayerState>(srcUrl ? 'loading' : 'idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Stable refs so the message listener is registered once and never torn down.
  const initSentRef = useRef(false)
  const pageIndexRef = useRef(0)

  const bridgePages = useMemo(
    () =>
      pages.map(p => ({
        id: p.id,
        title: p.title,
        blocks: p.blocks.map((b, i) => ({
          ...mapSlide(b, i),
          readingAudioUrl: b.readingAudioUrl,
        })),
      })),
    [pages],
  )
  const bridgePagesRef = useRef(bridgePages)
  useEffect(() => {
    bridgePagesRef.current = bridgePages
  }, [bridgePages])

  const initPayload = useMemo<{
    type: 'init'
    payload: BridgeFlashbookInitPayload
  }>(() => {
    const totalPoints = bridgePages.reduce(
      (sum, p) =>
        sum + p.blocks.reduce((s, b) => s + (b.isExercise ? b.points : 0), 0),
      0,
    )
    return {
      type: 'init',
      payload: {
        protocolVersion: 4,
        mode: 'preview' as const,
        locale: 'en',
        isRtl: false,
        user: {
          id: 'user',
          handle: '@user',
          displayName: 'User',
          avatarUri: null,
        },
        behaviour: {
          instantFeedback: true,
          shuffleOptions: false,
          showProgressBar: true,
        },
        book: {
          id: book.id,
          title: book.title,
          description: book.description ?? '',
          authorHandle: '@author',
          authorDisplayName: 'Author',
          ...(book.cover?.imageUri ? {coverImageUri: book.cover.imageUri} : {}),
        },
        pages: bridgePages,
        totalPages: bridgePages.length,
        totalPoints,
      },
    }
  }, [book, bridgePages])
  const initPayloadRef = useRef(initPayload)
  useEffect(() => {
    initPayloadRef.current = initPayload
  }, [initPayload])

  useEffect(() => {
    return () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({type: 'exit'}, '*')
      }
    }
  }, [])

  const sendToTheme = useCallback((msg: unknown) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
  }, [])
  const sendToThemeRef = useRef(sendToTheme)
  useEffect(() => {
    sendToThemeRef.current = sendToTheme
  }, [sendToTheme])

  const showPage = useCallback((index: number) => {
    const pagesRef = bridgePagesRef.current
    if (index < 0 || index >= pagesRef.length) return
    pageIndexRef.current = index
    sendToThemeRef.current({
      type: 'page_show',
      payload: {index, page: pagesRef[index], total: pagesRef.length},
    })
  }, [])

  const sendInit = useCallback(() => {
    if (initSentRef.current) return
    initSentRef.current = true
    sendToThemeRef.current(initPayloadRef.current)
    if (bridgePagesRef.current.length > 0) {
      pageIndexRef.current = 0
      sendToThemeRef.current({
        type: 'page_show',
        payload: {
          index: 0,
          page: bridgePagesRef.current[0],
          total: bridgePagesRef.current.length,
        },
      })
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'web') return

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      const msg = event.data as {type?: string; [k: string]: unknown}
      if (!msg || typeof msg.type !== 'string') return

      switch (msg.type) {
        case 'ready': {
          initSentRef.current = false
          sendInit()
          setState('running')
          break
        }

        case 'block_answer': {
          const p = (msg.payload ?? {}) as {
            pageId?: string
            blockId?: string
            value?: unknown
            timeSpentMs?: number
          }
          if (!p.pageId || !p.blockId) break
          const pagesRef = bridgePagesRef.current
          const page = pagesRef.find(pp => pp.id === p.pageId)
          const block = page?.blocks.find(b => b.id === p.blockId)
          if (!block) break

          const correct = block.isExercise
            ? checkAnswer(block, p.value ?? null)
            : true
          const pointsEarned = correct ? block.points : 0

          onBlockResult(p.pageId, p.blockId, block.type, {
            isCorrect: correct,
            points: pointsEarned,
            maxPoints: block.points,
            userAnswer: p.value ?? null,
            timeSpentMs: p.timeSpentMs ?? 0,
          })

          sendToThemeRef.current({
            type: 'feedback',
            payload: {
              pageId: p.pageId,
              blockId: p.blockId,
              correct,
              pointsEarned,
              pointsPossible: block.points,
              ...(correct ? {} : {correctValue: correctValueFor(block)}),
            },
          })
          break
        }

        case 'navigate_next': {
          showPage(pageIndexRef.current + 1)
          break
        }
        case 'navigate_prev': {
          showPage(pageIndexRef.current - 1)
          break
        }
        case 'navigate_to': {
          const {index} = (msg.payload ?? {}) as {index?: number}
          if (typeof index === 'number') showPage(index)
          break
        }

        case 'complete': {
          onComplete()
          break
        }

        case 'exit': {
          onClose()
          break
        }

        default:
          break
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onBlockResult, onComplete, onClose, sendInit, showPage])

  const handleIframeError = () => {
    setState('error')
    setErrorMsg('Failed to load the theme. Check the URL or file.')
  }

  // -- Native: not supported -------------------------------------------------
  if (Platform.OS !== 'web') {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🌐</Text>
        <Text style={s.title}>HTML Theme</Text>
        <Text style={s.desc}>
          HTML/CSS/JS themes render in the web version.{'\n'}
          On iOS/Android, choose a different theme.
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          style={s.btn}
          onPress={onClose}>
          <Text style={s.btnTxt}>Exit</Text>
        </TouchableOpacity>
      </View>
    )
  }

  if (!srcUrl) {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🎨</Text>
        <Text style={s.title}>No HTML theme configured</Text>
        <Text style={s.desc}>
          This FlashBook uses an HTML theme but no .html file is attached.
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          style={s.btn}
          onPress={onClose}>
          <Text style={s.btnTxt}>← Go back</Text>
        </TouchableOpacity>
      </View>
    )
  }

  if (state === 'error') {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>⚠️</Text>
        <Text style={s.title}>Theme failed to load</Text>
        <Text style={s.desc}>
          {errorMsg ?? 'The HTML theme could not be loaded.'}
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Retry loading the theme"
          accessibilityHint="Reloads the HTML theme from the start"
          style={s.btn}
          onPress={() => {
            initSentRef.current = false
            setErrorMsg(null)
            setState('loading')
            setReloadKey(k => k + 1)
          }}>
          <Text style={s.btnTxt}>Try again</Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Exit the book"
          accessibilityHint="Closes the reader"
          onPress={onClose}>
          <Text style={s.btnGhostTxt}>Exit</Text>
        </TouchableOpacity>
      </View>
    )
  }

  return (
    <View style={s.root}>
      {/* Slim host header: close only — the theme owns all in-page UI */}
      <View style={s.header}>
        <Text style={s.headerTitle} numberOfLines={1}>
          {book.title}
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onClose}
          style={s.closeBtn}>
          <Feather name="x" size={18} color={PLAY_SURFACE.text} />
        </TouchableOpacity>
      </View>

      {state === 'loading' && (
        <View style={s.loadingOverlay} pointerEvents="none">
          <ActivityIndicator size="large" color={PLAY_SURFACE.accent} />
        </View>
      )}

      {/* @ts-ignore -- web-only element */}
      <iframe
        key={reloadKey}
        ref={iframeRef}
        src={srcUrl}
        style={{width: '100%', flex: 1, border: 'none'}}
        sandbox="allow-scripts allow-same-origin allow-forms"
        allow="fullscreen"
        allowFullScreen
        title="FlashBook HTML Theme"
        onLoad={() => {
          if (state === 'loading') setState('running')
          // Race-condition fallback: blob URLs can fire `ready` before React
          // registers the message listener.
          setTimeout(() => {
            if (!initSentRef.current && iframeRef.current?.contentWindow) {
              initSentRef.current = true
              iframeRef.current.contentWindow.postMessage(
                initPayloadRef.current,
                '*',
              )
              if (bridgePagesRef.current.length > 0) {
                pageIndexRef.current = 0
                iframeRef.current.contentWindow.postMessage(
                  {
                    type: 'page_show',
                    payload: {
                      index: 0,
                      page: bridgePagesRef.current[0],
                      total: bridgePagesRef.current.length,
                    },
                  },
                  '*',
                )
              }
            }
          }, 300)
        }}
        onError={handleIframeError}
      />
    </View>
  )
}

// -- Styles -------------------------------------------------------------------

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: PLAY_SURFACE.page,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    display: 'flex' as any,
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: PLAY_SURFACE.page,
    borderBottomWidth: 1,
    borderBottomColor: PLAY_SURFACE.border,
    gap: 8,
  },
  headerTitle: {
    flex: 1,
    color: PLAY_SURFACE.text,
    fontSize: 14,
    fontWeight: '700',
  },
  closeBtn: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: PLAY_SURFACE.surface,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    alignItems: 'center' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    justifyContent: 'center' as any,
    backgroundColor: PLAY_SURFACE.page,
    zIndex: 10,
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 36,
    gap: 14,
    backgroundColor: PLAY_SURFACE.page,
  },
  icon: {fontSize: 52},
  title: {
    color: PLAY_SURFACE.text,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  desc: {
    color: PLAY_SURFACE.textMuted,
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 380,
  },
  btn: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 22,
    backgroundColor: PLAY_SURFACE.accent,
    minWidth: 220,
    alignItems: 'center',
  },
  btnTxt: {color: PLAY_SURFACE.onAccent, fontSize: 15, fontWeight: '700'},
  btnGhostTxt: {
    color: PLAY_SURFACE.textMuted,
    fontSize: 14,
    fontWeight: '600',
    paddingVertical: 8,
  },
})
